import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import { API_URL } from '../../utils/api';
import { hamtaBestallarnamn, hamtaSokbartKundnamn } from '../../utils/nameUtils';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessSubTab, SECTIONS } from '../../features/workspace/utils/rolePermissions';

/**
 * QuickMeetingProjectPicker
 *
 * Snabb-modal som öppnas från Navbarens "Möte"-knapp. Visar en sökbar lista
 * över BK:s/IA:s/admins projekt. Vid val navigerar vi till projektets
 * möten-flik med ?createMeeting=1 — då plockar MeetingsPage upp paramen
 * och öppnar dess egen create-meeting-modal automatiskt. Hela mötesflödet
 * (typ-val, agenda, deltagare, propose/book) hanteras där, utan
 * duplicering av handler-logik.
 */
const QuickMeetingProjectPicker = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/vecka/my-projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setProjects(json.data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Kunde inte hämta projekt');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Auto-focus sökfältet när modalen öppnas
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const t = setTimeout(() => inputRef.current.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Reset state vid stängning
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const filtered = projects.filter(p => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    // Både föreningen och kontaktpersonen ska ge träff
    const name = hamtaSokbartKundnamn(p.customer);
    const addr = (p.address || '').toLowerCase();
    const pid = (p.projectId || '').toLowerCase();
    return name.includes(q) || addr.includes(q) || pid.includes(q) || `#${pid}`.includes(q);
  });

  const handleSelect = (project) => {
    onClose();
    // Möten ligger i Projektering för BK, IA och admin, i Produktion för byggaren
    const sektion = canAccessSubTab(user?.role, SECTIONS.PROJEKTERING, 'moten') ? 'projektering' : 'produktion';
    navigate(`/workspace/${sektion}/${project._id}/moten?createMeeting=1`);
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium">
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Skapa möte</h2>
          <p className="text-xs text-gray-500 mt-0.5">Välj projekt — du landar direkt i möte-modalen.</p>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Sök på namn, adress eller #projekt-id..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-leadme-pink/30 focus:border-leadme-pink mb-3"
        />

        {error && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-3">{error}</div>
        )}

        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-lg">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-leadme-pink" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-6 text-sm text-gray-400 text-center">
              {projects.length === 0 ? 'Inga projekt hittades' : 'Inga träffar'}
            </div>
          ) : (
            filtered.slice(0, 100).map(p => (
              <button
                key={p._id}
                type="button"
                onClick={() => handleSelect(p)}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm border-b border-gray-100 last:border-0 transition-colors"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-gray-800">#{p.projectId}</span>
                  {(p.customer?.firstName || p.customer?.lastName) && (
                    <span className="text-gray-700">
                      {hamtaBestallarnamn(p.customer)}
                    </span>
                  )}
                </div>
                {p.address && (
                  <div className="text-xs text-gray-400 mt-0.5">{p.address}</div>
                )}
              </button>
            ))
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Avbryt
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuickMeetingProjectPicker;
